import React, { useState } from "react";
import {
    Card, CardBody, CardTitle, Button,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input
} from "reactstrap";
import { addAnnouncement, deleteAnnouncement } from "./UpdatesApi";

const ACCENT = "#534AB7";

const AnnouncementCard = ({ data = [], role, refresh }) => {
    const canManage = role === "HR Admin" || role === "Manager";
    const [selectedItem, setSelectedItem] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [newItem, setNewItem] = useState({ title: "", description: "", date: "" });
    const [hoveredId, setHoveredId] = useState(null);

    const openDetail = (item) => { setSelectedItem(item); setDetailOpen(true); };
    const closeDetail = () => { setSelectedItem(null); setDetailOpen(false); };
    const openAdd = () => setAddOpen(true);
    const closeAdd = () => { setAddOpen(false); setNewItem({ title: "", description: "", date: "" }); };

    const handleAdd = async () => {
        if (!newItem.title || !newItem.date) return;
        await addAnnouncement(newItem);
        closeAdd();
        refresh && refresh();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this announcement?")) return;
        await deleteAnnouncement(id);
        refresh && refresh();
    };

    return (
        <Card style={styles.card}>
            <CardBody style={styles.cardBody}>
                {/* Accent bar */}
                <div style={{ ...styles.accentBar, background: ACCENT }} />

                <div className="dashboard-scroll-area">
                    {/* Header */}
                    <div style={styles.header}>
                        <CardTitle tag="h6" style={styles.title}>
                            <div style={{ ...styles.iconWrap, background: "#EEEDFE" }}>
                                <i className="bx bxs-microphone" style={{ color: ACCENT, fontSize: "15px" }} />
                            </div>
                            Announcements
                        </CardTitle>
                        {canManage && (
                            <Button size="sm" onClick={openAdd} style={styles.addBtn}>+ Add</Button>
                        )}
                    </div>

                    {/* List */}
                    <ul className="list-unstyled mb-0">
                        {data.length ? data.map((a) => (
                            <li
                                key={a.id}
                                onClick={() => openDetail(a)}
                                onMouseEnter={() => setHoveredId(a.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                style={{
                                    ...styles.item,
                                    background: hoveredId === a.id ? "#F5F4FF" : "transparent",
                                }}
                            >
                                <div style={styles.itemLeft}>
                                    <span style={styles.itemTitle}>{a.title}</span>
                                    <small style={styles.itemDate}>
                                        {new Date(a.date).toLocaleDateString()}
                                    </small>
                                </div>
                                {canManage && (
                                    <Button
                                        color="danger" outline size="sm"
                                        style={styles.deleteBtn}
                                        onClick={(e) => { e.stopPropagation(); handleDelete(a.id); }}
                                    >
                                        Delete
                                    </Button>
                                )}
                            </li>
                        )) : <p style={styles.empty}>No announcements yet.</p>}
                    </ul>

                    {/* Footer */}
                    {data.length > 0 && (
                        <div style={styles.footer}>
                            <span style={{ ...styles.viewAll, color: ACCENT }}>View all →</span>
                        </div>
                    )}
                </div>
            </CardBody>

            {/* Detail Modal */}
            <Modal isOpen={detailOpen} toggle={closeDetail} centered>
                <ModalHeader toggle={closeDetail}>{selectedItem?.title}</ModalHeader>
                <ModalBody>
                    <p className="text-muted mb-2">
                        {selectedItem?.date && new Date(selectedItem.date).toLocaleDateString()}
                    </p>
                    <p className="mb-0">{selectedItem?.description || "No description."}</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeDetail}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Add Modal */}
            <Modal isOpen={addOpen} toggle={closeAdd} centered>
                <ModalHeader toggle={closeAdd}>Add Announcement</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Title</Label>
                            <Input value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} placeholder="Title" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input type="textarea" rows="3" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} placeholder="Optional description" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Date</Label>
                            <Input type="date" value={newItem.date} onChange={(e) => setNewItem({ ...newItem, date: e.target.value })} />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeAdd}>Cancel</Button>
                    <Button color="primary" onClick={handleAdd}>Save</Button>
                </ModalFooter>
            </Modal>
        </Card>
    );
};

const styles = {
    card: {
        borderRadius: "16px",
        border: "0.5px solid #e8e8e8",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        overflow: "hidden",
        marginBottom: "20px",
        position: "relative",
    },
    cardBody: { padding: "20px 20px 14px" },
    accentBar: {
        position: "absolute",
        top: 0, left: 0,
        width: "4px",
        height: "100%",
        borderRadius: "16px 0 0 16px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "14px",
    },
    title: {
        margin: 0,
        fontWeight: 600,
        fontSize: "14px",
        color: "#2C2C2A",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    iconWrap: {
        width: "30px",
        height: "30px",
        borderRadius: "8px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    addBtn: {
        background: "#534AB7",
        border: "none",
        borderRadius: "8px",
        fontSize: "12px",
        padding: "4px 12px",
        color: "#fff",
    },
    item: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 10px",
        borderRadius: "8px",
        cursor: "pointer",
        marginBottom: "4px",
        transition: "background 0.15s",
        borderBottom: "0.5px solid #f0f0f0",
    },
    itemLeft: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },
    itemTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#2C2C2A",
    },
    itemDate: {
        fontSize: "11px",
        color: "#888780",
    },
    deleteBtn: {
        fontSize: "11px",
        padding: "2px 8px",
        flexShrink: 0,
    },
    empty: {
        fontSize: "13px",
        color: "#888780",
        margin: 0,
    },
    footer: {
        borderTop: "0.5px solid #f0f0f0",
        marginTop: "10px",
        paddingTop: "8px",
        textAlign: "right",
    },
    viewAll: {
        fontSize: "12px",
        cursor: "pointer",
        fontWeight: 500,
    },
};

export default AnnouncementCard;